import { FC, ReactElement, memo, useCallback } from 'react'
import { WithUsername, WithTagname, WithPostUid } from 'interfaces/feed'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { isAuthenticatedSelector } from 'selectors/globalSelectors'
import { NavigationLink } from 'components/atoms/navigation/NavigationLink'
import { RETURN_URL_PARAM_NAME } from 'constants/pages'
import { FEED_POSTS_BASE_URL, FEED_AUTHORS_BASE_URL, FEED_TAGS_BASE_URL, OPPORTUNITIES_URL } from 'tangle-shared'
import { killAllFloatingPanesActionCreator } from 'actions/floatingPanesActions'

type ChildrenRenderer = (isAuthenticated: boolean, props: FeedLinkLoggedOutProps) => ReactElement | null
export type ReturnUrlResource =
  | ({ type: 'post'} & WithPostUid)
  | ({ type: 'user'} & WithUsername)
  | ({ type: 'tag' } & WithTagname)
  | ({ type: 'offers' })

export type FeedLinkLoggedOutProps =
  & ReturnUrlResource
  & {
    children: ChildrenRenderer | (ReactElement | null)
  }

const getReturnUrl = (props: FeedLinkLoggedOutProps): string | null => {
  if (props.type === 'post') {
    return `${ FEED_POSTS_BASE_URL }/${ props.postUid }`
  }

  if (props.type === 'user') {
    return `${ FEED_AUTHORS_BASE_URL }/${ props.username }`
  }

  if (props.type === 'tag') {
    return `${ FEED_TAGS_BASE_URL }/${ props.tagName }`
  }

  if (props.type === 'offers') {
    return OPPORTUNITIES_URL
  }

  return null
}

export const FeedLinkLoggedOut: FC<FeedLinkLoggedOutProps> = memo((props) => {
  const renderer = typeof props.children === 'function'
    ? props.children as ChildrenRenderer
    : () => <>{ props.children }</>

  const isAuthenticated = useStoreSelector(isAuthenticatedSelector) || false

  const dispatch = useStoreDispatch()
  const handleClick = useCallback(() => {
    dispatch(killAllFloatingPanesActionCreator())
  }, [dispatch])

  const children = renderer(isAuthenticated, props)
  if (isAuthenticated) {
    return children
  }

  const returnUrl = getReturnUrl(props)
  const search = returnUrl
    ? `?${ RETURN_URL_PARAM_NAME }=${ encodeURIComponent(returnUrl) }`
    : undefined

  return (
    <NavigationLink
      search={ search }
      to='onboarding-register'
      onClick={ handleClick }
    >
      { children }
    </NavigationLink>
  )
})
